import {
  advanceHintEscalation,
  createHintEscalationState,
  type HintEscalationState
} from "@/game/feedback";
import type { InputMethod } from "@/game/input";

import type { SequencePayload } from "./schema";

export type SequenceMoveDirection = "forward" | "backward";

export interface SequenceState {
  readonly initialOrder: readonly string[];
  readonly correctOrder: readonly string[];
  readonly order: readonly string[];
  readonly focusedItemId: string | null;
  readonly hintState: HintEscalationState;
  readonly moveCount: number;
  readonly retryCount: number;
  readonly lastInputMethod: InputMethod | null;
}

export type SequenceAction =
  | {
      readonly type: "focus-item";
      readonly itemId: string | null;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "move-item";
      readonly itemId: string;
      readonly direction: SequenceMoveDirection;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "keyboard-reorder";
      readonly direction: SequenceMoveDirection;
    }
  | { readonly type: "request-hint" }
  | { readonly type: "retry" }
  | { readonly type: "reset" };

export type SequenceHint =
  | {
      readonly stage: "gentle-hint";
      readonly targetIndex: number;
    }
  | {
      readonly stage: "reduced-choices";
      readonly targetIndex: number;
      readonly candidateItemIds: readonly string[];
    }
  | {
      readonly stage: "replay-demonstration";
      readonly order: readonly string[];
    };

export interface SequenceScore {
  readonly correctCount: number;
  readonly totalCount: number;
  readonly correctRate: number;
  readonly score: number;
  readonly completed: boolean;
  readonly hintCount: number;
  readonly retryCount: number;
  readonly moveCount: number;
}

function createStateFromOrders(
  initialOrder: readonly string[],
  correctOrder: readonly string[]
): SequenceState {
  return {
    initialOrder: [...initialOrder],
    correctOrder: [...correctOrder],
    order: [...initialOrder],
    focusedItemId: null,
    hintState: createHintEscalationState(),
    moveCount: 0,
    retryCount: 0,
    lastInputMethod: null
  };
}

export function createSequenceState(payload: SequencePayload): SequenceState {
  return createStateFromOrders(
    payload.items.map(({ id }) => id),
    payload.correctOrder
  );
}

function moveItem(
  state: SequenceState,
  itemId: string,
  direction: SequenceMoveDirection,
  inputMethod: InputMethod
): SequenceState {
  const itemIndex = state.order.indexOf(itemId);

  if (itemIndex === -1) {
    return state;
  }

  const offset = direction === "forward" ? 1 : -1;
  const targetIndex = itemIndex + offset;

  if (targetIndex < 0 || targetIndex >= state.order.length) {
    return state;
  }

  const targetItemId = state.order[targetIndex];

  if (targetItemId === undefined) {
    return state;
  }

  const order = [...state.order];
  order[itemIndex] = targetItemId;
  order[targetIndex] = itemId;

  return {
    ...state,
    order,
    focusedItemId: itemId,
    moveCount: state.moveCount + 1,
    lastInputMethod: inputMethod
  };
}

export function isSequenceComplete(state: SequenceState): boolean {
  return (
    state.order.length === state.correctOrder.length &&
    state.order.every(
      (itemId, index) => itemId === state.correctOrder[index]
    )
  );
}

export function reduceSequence(
  state: SequenceState,
  action: SequenceAction
): SequenceState {
  if (action.type !== "reset" && isSequenceComplete(state)) {
    return state;
  }

  switch (action.type) {
    case "focus-item":
      if (
        action.itemId !== null &&
        !state.order.includes(action.itemId)
      ) {
        return state;
      }

      if (
        state.focusedItemId === action.itemId &&
        state.lastInputMethod === action.inputMethod
      ) {
        return state;
      }

      return {
        ...state,
        focusedItemId: action.itemId,
        lastInputMethod: action.inputMethod
      };
    case "move-item":
      return moveItem(
        state,
        action.itemId,
        action.direction,
        action.inputMethod
      );
    case "keyboard-reorder":
      if (state.focusedItemId === null) {
        return state;
      }

      return moveItem(
        state,
        state.focusedItemId,
        action.direction,
        "keyboard"
      );
    case "request-hint":
      return {
        ...state,
        hintState: advanceHintEscalation(state.hintState)
      };
    case "retry":
      return {
        ...state,
        retryCount: state.retryCount + 1
      };
    case "reset":
      return createStateFromOrders(state.initialOrder, state.correctOrder);
  }
}

function findFirstMismatch(state: SequenceState): number {
  const comparableLength = Math.min(
    state.order.length,
    state.correctOrder.length
  );

  for (let index = 0; index < comparableLength; index += 1) {
    if (state.order[index] !== state.correctOrder[index]) {
      return index;
    }
  }

  return state.order.length === state.correctOrder.length
    ? -1
    : comparableLength;
}

export function getSequenceHint(state: SequenceState): SequenceHint | null {
  if (state.hintState.stage === "none" || isSequenceComplete(state)) {
    return null;
  }

  const targetIndex = findFirstMismatch(state);
  const currentItemId = state.order[targetIndex];
  const correctItemId = state.correctOrder[targetIndex];

  if (
    targetIndex === -1 ||
    currentItemId === undefined ||
    correctItemId === undefined
  ) {
    return null;
  }

  switch (state.hintState.stage) {
    case "gentle-hint":
      return {
        stage: "gentle-hint",
        targetIndex
      };
    case "reduced-choices":
      return {
        stage: "reduced-choices",
        targetIndex,
        candidateItemIds: [currentItemId, correctItemId]
      };
    case "replay-demonstration":
      return {
        stage: "replay-demonstration",
        order: [...state.correctOrder]
      };
  }
}

export function scoreSequence(state: SequenceState): SequenceScore {
  const totalCount = state.correctOrder.length;
  let correctCount = 0;

  for (let index = 0; index < totalCount; index += 1) {
    if (state.order[index] === state.correctOrder[index]) {
      correctCount += 1;
    }
  }

  const correctRate = totalCount === 0 ? 0 : correctCount / totalCount;
  const completed =
    totalCount > 0 &&
    state.order.length === totalCount &&
    correctCount === totalCount;

  return {
    correctCount,
    totalCount,
    correctRate,
    score: Math.round(correctRate * 100),
    completed,
    hintCount: state.hintState.hintCount,
    retryCount: state.retryCount,
    moveCount: state.moveCount
  };
}
